import React, { useCallback, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Typography,
    type DialogProps
} from '@mui/material';

import authApi from '../../../api/auth/api';
import { getPasswordText } from '../utils/getPasswordText';
import { type ReasonType } from '../../../api/auth/helpers/helpers';
import { PasswordVisibilityIcon } from './PasswordVisibilityIcon';

interface Props {
    open: boolean;
    maxWidth?: DialogProps['maxWidth'];
    onClose: () => void;
    reason?: ReasonType;
}

const MIN_PASSWORD_LENGTH = 6;

const SetPasswordDialog: React.FC<Props> = ({ open, maxWidth = 'xs', onClose, reason = 'no_password' }) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { title, description } = getPasswordText(reason);

    const handleSubmit = useCallback(async () => {
        setError('');
        if (password.length < MIN_PASSWORD_LENGTH) {
            setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        setLoading(true);
        try {
            await authApi.setPassword({ password });
            setPassword('');
            setConfirmPassword('');
            onClose();
        } catch (err) {
            console.error(err);
            setError('Failed to set password. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [password, confirmPassword, onClose]);

    const canClose = reason === 'user_change';

    return (
        <Dialog
            open={open}
            onClose={canClose ? onClose : undefined}
            disableEscapeKeyDown={!canClose}
            maxWidth={maxWidth}
            fullWidth
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText>{description}</DialogContentText>
                {error && (
                    <Typography color="error" variant="body2" style={{ marginBottom: 8 }}>
                        {error}
                    </Typography>
                )}
                <TextField
                    autoFocus
                    margin="dense"
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    variant="outlined"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowPassword((s) => !s)}>
                                    {<PasswordVisibilityIcon showPassword={showPassword} />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <TextField
                    margin="dense"
                    label="Confirm Password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    variant="outlined"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSubmit();
                    }}
                />
            </DialogContent>
            <DialogActions>
                {canClose && (
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                )}
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    disabled={loading || !password || !confirmPassword}
                >
                    {loading ? 'Saving...' : 'Set Password'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SetPasswordDialog;
